import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
} from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, addDays, parseISO, isValid } from "date-fns";
import { Plane, MapPin, CalendarDays, Tag } from "lucide-react";
import "../index.css";
import { CustomTimePicker } from "./CustomTimePicker";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { createPortal } from "react-dom";


const theme = createTheme({
  palette: {
    primary: {
      main: "#ca8032",
    },
  },
});

const customDateStyles = `
  .react-datepicker-wrapper {
    width: 100%;
    display: block;
  }
    
  // .react-datepicker-popper {
  //   z-index: 50 !important;
  // }
  .react-datepicker {
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
    background-color: white;
    font-size: 0.85rem;
  }
  .react-datepicker__header {
    background-color: white;
    border-bottom: 1px solid #f3f4f6;
    padding-top: 8px;
    position: relative;
  }
  .react-datepicker__navigation {
    top: 10px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .react-datepicker__navigation:hover {
    background-color: #f3f4f6;
  }
  .react-datepicker__navigation-icon::before {
    border-color: #ca8032;
    border-width: 2px 2px 0 0;
  }
  .react-datepicker__current-month {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
  }
  .react-datepicker__day-name {
    color: #9ca3af;
    width: 2rem;
    margin: 0;
  }
  .react-datepicker__day {
    width: 2rem;
    line-height: 2rem;
    margin: 1px;
    border-radius: 50%;
  }
  .react-datepicker__day--selected,
  .react-datepicker__day--range-end,
  .react-datepicker__day--range-start {
    background-color: #ca8032 !important;
    color: white !important;
  }
  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range {
    background-color: #fff1e0 !important;
    color: #ca8032 !important;
    border-radius: 0;
  }
  .react-datepicker__day--range-start {
    border-top-left-radius: 50% !important;
    border-bottom-left-radius: 50% !important;
  }
  .react-datepicker__day--range-end {
    border-top-right-radius: 50% !important;
    border-bottom-right-radius: 50% !important;
  }
  .react-datepicker__triangle { display: none; }

  /* --- PORTAL FIX --- */
  #calendar-portal .react-datepicker-popper {
    position: relative !important;
    inset: auto !important;
    transform: none !important;
    padding-top: 0 !important;
    pointer-events: auto;
  }

 /* Default behavior — scrolls with form */
.react-datepicker-popper {
  position: absolute !important;
  z-index: 999999 !important;
}

@media (max-width: 640px) {
  .react-datepicker-popper {
    left: 50% !important;
    top: calc(100% + 12px) !important;

    transform: translateX(-50%) !important;

    width: 90vw;
    max-width: 90vw;

    right: auto !important;
    bottom: auto !important;
  }
}





  // @media (max-width: 640px) {
  //   .react-datepicker-popper {
  //     width: 85vw;
  //     left: 50% !important;
  //     right: auto !important;
  //     transform: translateX(-50%) !important;
  //   }
    .react-datepicker {
      width: 100%;
      max-width: 100% !important;
      font-size: 0.9rem;
      margin: 0;
      padding: 0.5rem !important;
      overflow: hidden !important;
    }
    .react-datepicker__month-container {
      width: 100% !important;
    }
    .react-datepicker__month {
      width: 100% !important;
      padding: 0.25rem !important;
    }
    .react-datepicker__day-names {
      display: flex !important;
      flex-direction: row !important;
      justify-content: space-between !important;
      width: 100% !important;
      flex-wrap: nowrap !important;
      margin-bottom: 0.25rem !important;
    }
    .react-datepicker__week {
      display: flex !important;
      justify-content: space-between !important;
      margin-bottom: 0.125rem !important;
    }
    .react-datepicker__day {
      flex: 1 !important;
      margin: 0.125rem !important;
      width: auto !important;
      line-height: 2.5rem;
      height: 2.5rem;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      min-width: 0 !important;
      font-size: 1rem !important;
      padding: 0.25rem !important;
    }
    .react-datepicker__day-name {
      flex: 1 !important;
      margin: 0 !important;
      width: auto !important;
      font-size: 1rem !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      min-width: 0 !important;
      height: 2rem !important;
      padding: 0.25rem !important;
    }
    .react-datepicker__current-month {
      font-size: 1.1rem;
      padding-bottom: 0.5rem !important;
    }
  }
`;

// ... HELPER FUNCTIONS ...
const convertTo24Hour = (time12) => {
  if (!time12) return "00:00";
  const [time, modifier] = time12.split(" ");
  let [hours, minutes] = time.split(":");
  if (hours === "12") hours = "00";
  if (modifier === "PM") hours = parseInt(hours, 10) + 12;
  return `${String(hours).padStart(2, "0")}:${minutes}`;
};
const convertTo12Hour = (time24) => {
  if (!time24) return "12:00 AM";
  const [hours, minutes] = time24.split(":").map(Number);
  const modifier = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  return `${String(formattedHours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )} ${modifier}`;
};
const generateTimeOptions = () => {
  const times = [];
  for (let i = 0; i < 24 * 60; i += 30) {
    const date = new Date(2000, 0, 1, Math.floor(i / 60), i % 60);
    times.push(format(date, "hh:mm a"));
  }
  return times;
};
const combineDateAndTime = (date, time12) => {
  if (!date || !time12) return "";
  return `${format(date, "yyyy-MM-dd")}T${convertTo24Hour(time12)}`;
};

// --- CUSTOM DATE INPUT WITH ICON ---
const CustomDateInput = forwardRef(
  ({ value, onClick, label, displayValue }, ref) => {
    const inputValue = displayValue !== undefined ? displayValue : value;

    return (
      <div className="relative cursor-pointer" onClick={onClick} ref={ref}>
        {/* Icon */}
        <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#ca8032] pointer-events-none z-10" />

        <input
          value={inputValue || ""}
          readOnly
          placeholder=" "
          /* Changed pl-10 to pl-12 (48px padding) */
          className="peer w-full border border-gray-300 rounded-md pl-12 pr-4 pt-6 pb-1 text-base h-14 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none bg-white cursor-pointer caret-transparent truncate"
        />

        {/* Changed left-10 to left-11 (44px + px-1 = 48px start) to match input text */}
        <label className="absolute text-[#ca8032] left-11 top-1/2 -translate-y-1/2 transition-all duration-200 peer-focus:top-2 peer-focus:text-[0.7rem] peer-focus:-translate-y-0 peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-[0.7rem] peer-[:not(:placeholder-shown)]:-translate-y-0 pointer-events-none bg-white px-1">
          {label}
        </label>
      </div>
    );
  }
);

export default function FormAtHome({ isEditMode = false, onClose = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const calendarRef = useRef(null);
  const pickupLocationRef = useRef(null);
  const dropoffLocationRef = useRef(null);

  const [sameLocation, setSameLocation] = useState(true);
  const [pickupQuery, setPickupQuery] = useState("");
  const [dropoffQuery, setDropoffQuery] = useState("");
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [pickupLocationObj, setPickupLocationObj] = useState(null);
  const [dropoffLocationObj, setDropoffLocationObj] = useState(null);

  const [dateRange, setDateRange] = useState([
    addDays(new Date(), 1),
    addDays(new Date(), 2),
  ]);
  const [startDate, endDate] = dateRange;

  const [pickupTime, setPickupTime] = useState("11:30 AM");
  const [dropoffTime, setDropoffTime] = useState("11:30 AM");
  const timeOptions = generateTimeOptions();

  const [pickupError, setPickupError] = useState("");
  const [dropoffError, setDropoffError] = useState("");
  const [dateTimeError, setDateTimeError] = useState("");
  const [monthsShown, setMonthsShown] = useState(2);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  // --- Logic ---
  const fetchLocations = async (query, setSuggestions) => {
    if (query.length < 2) return setSuggestions([]);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${baseUrl}get_country_city_airport_name`);
      const filtered = res.data.filter((loc) =>
        `${loc.city} ${loc.airport_name} ${loc.country_name} ${loc.airport_code}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
      setSuggestions(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  const debouncedFetchPickup = useCallback(
    debounce((query) => fetchLocations(query, setPickupSuggestions), 300),
    []
  );
  const debouncedFetchDropoff = useCallback(
    debounce((query) => fetchLocations(query, setDropoffSuggestions), 300),
    []
  );

  const handleSelectPickup = (loc) => {
    setPickupQuery(
      `${loc.city} - ${loc.airport_name} (${loc.airport_code}), ${loc.country_name}`
    );
    setPickupLocationObj(loc);
    setShowPickupSuggestions(false);
  };
  const handleSelectDropoff = (loc) => {
    setDropoffQuery(
      `${loc.city} - ${loc.airport_name} (${loc.airport_code}), ${loc.country_name}`
    );
    setDropoffLocationObj(loc);
    setShowDropoffSuggestions(false);
  };

  // Initialization & Event Listeners
  useEffect(() => {
    if (isEditMode || searchParams.toString()) {
      const pickupLoc = searchParams.get("pickup_location") || "";
      const dropoffLoc = searchParams.get("dropoff_location") || "";
      const pickupDateStr = searchParams.get("pickup_date");
      const dropoffDateStr = searchParams.get("dropoff_date");
      const sameLocParam = searchParams.get("same_location");

      setPickupQuery(pickupLoc);
      setDropoffQuery(dropoffLoc);
      setSameLocation(sameLocParam ? sameLocParam === "true" : true);

      const pDate = pickupDateStr ? parseISO(pickupDateStr) : null;
      const dDate = dropoffDateStr ? parseISO(dropoffDateStr) : null;

      if (pDate && isValid(pDate) && dDate && isValid(dDate)) {
        setDateRange([pDate, dDate]);
        setPickupTime(convertTo12Hour(pickupDateStr.split("T")[1]));
        setDropoffTime(convertTo12Hour(dropoffDateStr.split("T")[1]));
      }
      if (pickupLoc)
        setPickupLocationObj({
          airport_code: searchParams.get("pickup_airport_code"),
          city: searchParams.get("pickup_city"),
          country_name: searchParams.get("pickup_country"),
          airport_name: searchParams.get("pickup_airport_name"),
        });
      if (dropoffLoc)
        setDropoffLocationObj({
          airport_code: searchParams.get("dropoff_airport_code"),
          city: searchParams.get("dropoff_city"),
          country_name: searchParams.get("dropoff_country"),
          airport_name: searchParams.get("dropoff_airport_name"),
        });
    }
  }, [isEditMode, searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pickupLocationRef.current &&
        !pickupLocationRef.current.contains(event.target)
      )
        setShowPickupSuggestions(false);
      if (
        dropoffLocationRef.current &&
        !dropoffLocationRef.current.contains(event.target)
      )
        setShowDropoffSuggestions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setMonthsShown(width < 640 ? 1 : 2);
      setIsLargeScreen(width >= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasErrors = false;
    setPickupError("");
    setDropoffError("");
    setDateTimeError("");

    if (!pickupLocationObj) {
      setPickupError("Please select a valid pickup location.");
      hasErrors = true;
    }
    if (!sameLocation && !dropoffLocationObj) {
      setDropoffError("Please select a valid drop-off location.");
      hasErrors = true;
    }
    if (!startDate || !endDate) {
      setDateTimeError("Please select a pickup and drop-off date.");
      hasErrors = true;
    } else {
      const finalStart = new Date(combineDateAndTime(startDate, pickupTime));
      const finalEnd = new Date(combineDateAndTime(endDate, dropoffTime));
      if (finalEnd <= finalStart) {
        setDateTimeError("Drop-off date and time must be after pickup.");
        hasErrors = true;
      }
    }

    if (hasErrors) return;

    const dropLocObj = sameLocation ? pickupLocationObj : dropoffLocationObj;
    const dropQuery = sameLocation ? pickupQuery : dropoffQuery;
    const params = new URLSearchParams({
      pickup_date: combineDateAndTime(startDate, pickupTime),
      pickup_location: pickupQuery,
      pickup_airport_code: pickupLocationObj.airport_code,
      pickup_city: pickupLocationObj.city,
      pickup_country: pickupLocationObj.country_name,
      pickup_airport_name: pickupLocationObj.airport_name,
      dropoff_date: combineDateAndTime(endDate, dropoffTime),
      dropoff_location: dropQuery,
      dropoff_airport_code: dropLocObj.airport_code,
      dropoff_city: dropLocObj.city,
      dropoff_country: dropLocObj.country_name,
      dropoff_airport_name: dropLocObj.airport_name,
      same_location: sameLocation,
      promo_code: showCoupon ? couponCode : ""// Je added de line POUR CODE DE CUPON
    });
    navigate(`/results?${params.toString()}`);
    if (isEditMode && onClose) onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <style>{customDateStyles}</style>

      <div
        className={
          isEditMode
            ? ""
            : "max-w-screen-xl mx-auto flex justify-center px-2 py-2 "
        }
      >
        <div
          className={
            isEditMode
              ? ""
              : "bg-white p-6 lg:p-8 rounded-xl border border-2 border-orange-400/50 shadow-lg w-full relative z-[50]"
          }
        >
          <form onSubmit={handleSubmit}>
            {/* --- LAYOUT CONTAINER --- */}
            <div className="relative grid grid-cols-1 md:grid-cols-2 lg:flex lg:flex-row gap-2 items-end">
              {/* 1. PICKUP LOCATION */}
              <div
                ref={pickupLocationRef}
                className={`col-span-2 ${sameLocation ? "lg:flex-[2]" : "lg:flex-[1.5]"
                  }`}
              >
                <div className="h-5">
                  {pickupError && (
                    <p className="text-red-500 text-xs font-medium">
                      {pickupError}
                    </p>
                  )}
                </div>
                <div className="relative">
                  <MapPin className="absolute text-[#ca8032] left-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none z-10" />
                  <input
                    autoComplete="off"
                    id="pickup-location"
                    value={pickupQuery}
                    onChange={(e) => {
                      setPickupQuery(e.target.value);
                      setPickupLocationObj(null);
                      setShowPickupSuggestions(true);
                      debouncedFetchPickup(e.target.value);
                    }}
                    onFocus={() => {
                      setShowPickupSuggestions(true);
                      setPickupError("");
                    }}
                    placeholder=" "
                    className="peer w-full border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-1 text-base h-14 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none truncate"
                  />
                  <label
                    htmlFor="pickup-location"
                    className="absolute text-gray-600 left-10 top-1/2 -translate-y-1/2 transition-all duration-200 peer-focus:top-2 peer-focus:text-[0.7rem] peer-focus:-translate-y-0 peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-[0.7rem] peer-[:not(:placeholder-shown)]:-translate-y-0 pointer-events-none bg-white px-1"
                  >
                    Pickup Location
                  </label>
                  {showPickupSuggestions && pickupSuggestions.length > 0 && (
                    <ul className="absolute z-30 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto mt-1">
                      {pickupSuggestions.map((loc) => (
                        <li
                          key={loc.airport_code}
                          onClick={() => handleSelectPickup(loc)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                        >
                          <Plane className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span>
                            {loc.city} - {loc.airport_name} ({loc.airport_code}
                            ), {loc.country_name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* 1b. DROPOFF LOCATION */}
              {!sameLocation && (
                <div
                  ref={dropoffLocationRef}
                  className="col-span-2 lg:flex-[1.5]"
                >
                  <div className="h-5">
                    {dropoffError && (
                      <p className="text-red-500 text-xs font-medium">
                        {dropoffError}
                      </p>
                    )}
                  </div>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#ca8032] pointer-events-none z-10" />
                    <input
                      autoComplete="off"
                      id="dropoff-location"
                      value={dropoffQuery}
                      onChange={(e) => {
                        setDropoffQuery(e.target.value);
                        setDropoffLocationObj(null);
                        setShowDropoffSuggestions(true);
                        debouncedFetchDropoff(e.target.value);
                      }}
                      onFocus={() => {
                        setShowDropoffSuggestions(true);
                        setDropoffError("");
                      }}
                      placeholder=" "
                      className="peer w-full border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-1 text-base h-14 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none truncate"
                    />
                    <label
                      htmlFor="dropoff-location"
                      className="absolute text-gray-600 left-10 top-1/2 -translate-y-1/2 transition-all duration-200 peer-focus:top-2 peer-focus:text-[0.7rem] peer-focus:-translate-y-0 peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-[0.7rem] peer-[:not(:placeholder-shown)]:-translate-y-0 pointer-events-none bg-white px-1"
                    >
                      Drop-off Location
                    </label>
                    {showDropoffSuggestions &&
                      dropoffSuggestions.length > 0 && (
                        <ul className="absolute z-30 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto mt-1">
                          {dropoffSuggestions.map((loc) => (
                            <li
                              key={loc.airport_code}
                              onClick={() => handleSelectDropoff(loc)}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                            >
                              <Plane className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span>
                                {loc.city} - {loc.airport_name} (
                                {loc.airport_code}), {loc.country_name}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              )}

              {/* 2. PICKUP DATE (DatePicker) */}
              <div className="col-span-1 lg:flex-1 min-w-0">
                <DatePicker
                  ref={calendarRef}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update);
                    setDateTimeError("");
                  }}
                  popperPlacement="bottom-start"
                  popperClassName="react-datepicker-popper"
                  minDate={new Date()}
                  monthsShown={monthsShown}
                  isClearable={false}

                  // Use the built-in Portal for mobile (below 640px) 
                  // This centers it automatically and adds a backdrop
                  withPortal={window.innerWidth < 640}

                  // Keep your portal for desktop to avoid clipping
                  popperContainer={window.innerWidth >= 640 ? ({ children }) => createPortal(children, document.body) : undefined}

                  customInput={
                    <CustomDateInput
                      label="Pickup Date"
                      displayValue={startDate ? format(startDate, "MMM d, yyyy") : ""}
                    />
                  }
                />
              </div>

              {/* 3. PICKUP TIME */}
              <div className="col-span-1 lg:w-32">
                <CustomTimePicker
                  label="Time"
                  value={pickupTime}
                  options={timeOptions}
                  onChange={(e) => {
                    setPickupTime(e.target.value);
                    setDateTimeError("");
                  }}
                />
              </div>

              {/* 4. DROPOFF DATE (Manual Input) */}
              <div className="col-span-1 lg:flex-1 min-w-0">
                <CustomDateInput
                  label="Drop-off Date"
                  displayValue={endDate ? format(endDate, "MMM d, yyyy") : ""}
                  onClick={() => {
                    if (calendarRef.current) {
                      calendarRef.current.setOpen(true);
                    }
                  }}
                />
              </div>

              {/* 5. DROPOFF TIME */}
              <div className="col-span-1 lg:w-32">
                <CustomTimePicker
                  label="Time"
                  value={dropoffTime}
                  options={timeOptions}
                  onChange={(e) => {
                    setDropoffTime(e.target.value);
                    setDateTimeError("");
                  }}
                />
              </div>

              {/* 6. SEARCH BUTTON */}
              <div className="col-span-2 lg:w-auto">
                <button
                  type="submit"
                  className="btn px-8 text-white rounded-lg bg-[#ca8032] w-full h-14 text-lg hover:bg-[#b5702a] transition-colors font-semibold flex items-center justify-center"
                >
                  {isEditMode ? "Update" : "Search"}
                </button>
              </div>

              <div
                id="calendar-portal"
                className="absolute top-full left-0 w-full flex justify-center z-50 mt-2 pointer-events-none"
              ></div>
            </div>
            {/* --- END LAYOUT CONTAINER --- */}

            <div className="h-5">
              {dateTimeError && (
                <p className="text-red-500 text-xs mt-2 font-medium">
                  {dateTimeError}
                </p>
              )}
            </div>


            {/* /toggles/ */}

            {/* --- BOTTOM SECTION: TOGGLES PUSHED TO CORNERS --- */}
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">

              {/* LEFT CORNER: Same Location Toggle */}
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">
                  Return to same location
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameLocation}
                    onChange={() => {
                      if (!sameLocation) setDropoffError("");
                      setSameLocation(!sameLocation);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-[#ca8032]"></div>
                  <div className="absolute top-[2px] left-[2px] bg-white border-gray-300 border rounded-full h-5 w-5 transition-all peer-checked:translate-x-full"></div>
                </label>
              </div>

              {/* RIGHT CORNER: Coupon Toggle + Input */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">
                    Have a coupon code?
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCoupon}
                      onChange={() => setShowCoupon(!showCoupon)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-[#ca8032]"></div>
                    <div className="absolute top-[2px] left-[2px] bg-white border-gray-300 border rounded-full h-5 w-5 transition-all peer-checked:translate-x-full"></div>
                  </label>
                </div>

                {/* Coupon Input (Sliding in from the right) */}
                {showCoupon && (
                  <div className="relative animate-in fade-in slide-in-from-right-4 duration-300">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter Code"
                      className="w-32 sm:w-40 border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none h-10 uppercase font-semibold"
                    />
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </ThemeProvider>
  );
}
